import View                       from '@widesk-ui/components/View';
import Image                      from '@widesk-ui/components/Image';
import React                      from 'react';
import Segmented                  from '@widesk-ui/Segmented';
import useDesignTokens            from '@/hooks/useDesignTokens';
import { EyeOutlined }            from '@ant-design/icons';
import { DesktopOutlined }        from '@ant-design/icons';
import { MobileOutlined }         from '@ant-design/icons';
import mapPreview                 from '@/TokamapEditor/ux/assets/map-preview.png';
import type { PlacePanelEntry }   from '@/TokamapEditor/helpers/placePanelEntry';
import { groupConsecutiveImages } from '@/TokamapEditor/helpers/placePanelEntry';
import storageImageCrossOrigin    from '@/helpers/storageImageCrossOrigin';
import { corsImageSrc }           from '@/helpers/storageImageCrossOrigin';

/**
 * Aperçu du panneau que verra le visiteur, posé sur un plan de démonstration.
 *
 * Il ne remplace pas la prévisualisation grandeur nature (`PreviewModal`, qui ouvre la vraie carte) :
 * il répond à la question qu'on se pose en REMPLISSANT la fiche, sans la quitter — « qu'est-ce que ça
 * donne ? ». D'où la scène complète plutôt qu'une simple liste : c'est le fait de voir le panneau
 * couvrir un plan qui rend concret ce qu'on est en train d'écrire.
 *
 * Les règles d'affichage (titre remplacé, images affichées, champs vides masqués) viennent de
 * `buildPlacePanelEntry`, partagé avec `PreviewModal`.
 *
 * La géométrie et la typographie reprennent celles du vrai panneau — voir `.toka-entity-panel` dans
 * `tokamap-editor/packages/core/src/ui/style.css` : image 48px, titre 16/700, sous-titre 13,
 * intitulés de champs en 11 majuscules espacées au-dessus d'un filet. Tout est ici à l'échelle
 * ~0,75, la sidebar faisant 400px de large là où le panneau en fait 320 sur un écran de bureau.
 *
 * REPRODUITES et non réutilisées, faute de mieux. Réutiliser la feuille de style de `@tokamap/core`
 * supposerait de rendre la scène à sa taille réelle dans un conteneur `inline-size` — c'est la seule
 * façon d'obtenir les bons `@container` — puis de la réduire par `transform: scale()`. Le facteur
 * serait alors d'environ 0,33 dans une sidebar de 400px : le titre tomberait à 5px, illisible. Le
 * panneau réel étant construit en DOM impératif (`EntityPanel.ts`), il n'y a pas non plus de
 * composant à réemployer. Ce qui PEUT être partagé l'est : les règles d'affichage
 * (`buildPlacePanelEntry`), et rien d'autre ne l'est en double.
 *
 * Les deux appareils ne changent pas que la largeur : le vrai panneau bascule de layout à 700px de
 * CONTENEUR (une `@container`, pas une `@media`). Au-dessus, il flotte à gauche, encarté de 16px et
 * arrondi de partout ; en dessous, il devient une feuille du bas, pleine largeur, arrondie en haut
 * seulement, bornée à 70% de la hauteur et coiffée d'une poignée. C'est ce qui rend le sélecteur
 * utile : sur mobile, le panneau MASQUE le plan, et un titre trop long y coûte plus cher.
 *
 * Couleurs fixes et non celles du thème de l'éditeur : cette vignette représente le site PUBLIÉ. La
 * teinter en sombre parce que le backoffice l'est aurait montré autre chose que ce qui sera en
 * ligne.
 *
 * Rien ici ne dépend des réglages de la CARTE ouverte, bien qu'ils soient lisibles : ni le fond (sa
 * couleur, sa carte du monde), ni le bouton « Y aller » (sa géolocalisation). Un point d'intérêt et
 * un type appartiennent au PROJET, et un projet porte plusieurs cartes, chacune réglée à sa façon —
 * les reprendre reviendrait à présenter comme « ce que verra le visiteur » ce qu'il verra sur UNE
 * des cartes, en taisant les autres. L'aperçu s'en tient donc à ce qui est vrai partout : le titre,
 * le sous-titre et les champs. L'illustration, elle, ne prétend rien sur aucune carte.
 *
 * L'illustration est une IMAGE de plan, pas une carte vivante : une vraie carte MapLibre ici coûterait un
 * contexte WebGL par ouverture de fiche, sans jamais le rendre — `createTokaMap` n'expose aucun
 * `destroy()` et `TokaMap` assume de n'être monté qu'une fois par page (voir la garde StrictMode
 * dans `TokaMap.tsx`, et son `window.tokamap = instance`, qu'une seconde instance écraserait). Elle
 * donne l'échelle et le contexte ; la vraie carte reste dans la prévisualisation grandeur nature.
 *
 * Et elle est traitée pour qu'on ne la prenne pas pour le plan du projet : floutée et désaturée,
 * avec la mention « Illustration » posée dessus. L'ordre compte — c'est le FLOU qui fait le travail,
 * la mention ne fait que confirmer : on regarde une image avant d'en lire la légende, et un fond net
 * aurait laissé croire qu'on regarde sa propre carte. C'est le genre de malentendu qu'on ne découvre
 * qu'une fois que quelqu'un s'étonne que sa carte ne ressemble pas à ça. Le flou concentre du même
 * coup l'attention sur le panneau, qui est le sujet.
 */
export default function PlacePanelPreview(props: {
	entry: PlacePanelEntry;
	image?: string;
}) {
	const tokens = useDesignTokens();
	const { entry } = props;

	const [device, setDevice] = React.useState<'desktop' | 'mobile'>('desktop');
	const mobile = device === 'mobile';

	/**
	 * Le panneau est défilé jusqu'en bas quand on change d'appareil : le mobile est nettement plus
	 * haut, et sa feuille du bas — la partie qu'on veut justement regarder — tombait sous le pli de la
	 * sidebar.
	 *
	 * On écrit dans le conteneur plutôt que d'appeler `scrollIntoView` sur le cadre : cette dernière
	 * voie a été essayée et vérifiée dans le navigateur, elle partait avec les bons arguments sur le
	 * bon élément et ne déplaçait rien — trop de façons d'échouer en silence (mise en page pas encore
	 * calculée, nœud remplacé entre-temps). Un `scrollTop` affecté ne peut pas être ignoré.
	 *
	 * Pas au montage : ouvrir une fiche ne doit pas la faire défiler d'emblée jusqu'à l'aperçu, qui
	 * est la dernière chose de l'écran.
	 */
	const frameRef = React.useRef<HTMLDivElement>(null);
	const mountedRef = React.useRef(false);

	React.useEffect(() => {
		if (!mountedRef.current) {
			mountedRef.current = true;
			return;
		}

		const scrollToBottom = () => {
			let node: HTMLElement | null = frameRef.current;

			while (node) {
				const { overflowY } = window.getComputedStyle(node);

				if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
					// Défilement INSTANTANÉ, pas animé : `behavior: 'smooth'` ne produit rien ici — vérifié
					// dans le navigateur, le conteneur reste à 0 là où `auto` défile aussitôt. Ce panneau se
					// redessine en continu (`updateOnChange` du formulaire), et un défilement animé est
					// annulé dès que la boîte qui défile est remise en page sous lui.
					node.scrollTo({ top: node.scrollHeight, behavior: 'auto' });
					return;
				}

				node = node.parentElement;
			}
		};

		// Tout de suite, PUIS une fois la mise en page reposée. Le premier appel suffit dans le cas
		// courant ; le second rattrape le cas où la nouvelle hauteur du cadre n'est pas encore prise en
		// compte. Rien n'est annulé au nettoyage : un `requestAnimationFrame` annulé par l'effet suivant
		// était justement la raison pour laquelle ce défilement ne partait jamais — vérifié dans le
		// navigateur, la fonction était programmée puis jetée avant de s'exécuter.
		scrollToBottom();

		const timeout = setTimeout(scrollToBottom, 120);

		return () => clearTimeout(timeout);
	}, [device]);

	return (
		// Réserve sous le cadre en mobile. L'aperçu est le dernier bloc de la fiche, donc le défilement
		// s'arrête au bas du panneau : c'est cette réserve, et elle seule, qui décide de ce qu'on voit
		// sous la feuille. Assez pour qu'elle ne soit pas au ras du bord, pas au point de laisser un
		// vide sous elle.
		<View gap="xs" style={{ paddingBottom: mobile ? 16 : undefined }}>
			<View row centerV gap="xs" size="small" color={tokens.text.muted}>
				<EyeOutlined />

				<View flex>Aperçu — ce que verra le visiteur</View>

				<Segmented
					size="small"
					value={device}
					onChange={value => setDevice(value as 'desktop' | 'mobile')}
					options={[
						{ value: 'desktop', label: <DesktopOutlined />, title: 'Bureau' },
						{ value: 'mobile', label: <MobileOutlined />, title: 'Mobile' },
					]}
				/>
			</View>

			{/* Un `div` et non un `View` : `View` n'est pas un `forwardRef`, il ignore donc `ref` — et
			    `frameRef.current` serait resté `null`, ce qui rendait le défilement muet. */}
			<div
				ref={frameRef}
				style={{
					position: 'relative',
					borderRadius: 10,
					// Le cadre du mobile est plus HAUT que celui du bureau : à largeur de téléphone, la
					// feuille du bas n'occupe que 70% d'une vignette déjà étroite, et le peu qu'elle montre
					// devenait illisible. Proportions d'un téléphone (375 × 600), la silhouette faisant
					// autant pour la lecture que le changement de layout.
					height: mobile ? 320 : 236,
					width: mobile ? 200 : undefined,
					alignSelf: mobile ? 'center' : undefined,
					overflow: 'hidden',
					border: `1px solid ${tokens.border.default}`,
					background: '#E9EBEF',
				}}
			>
					{/* Le fond dans sa propre couche : le filtre s'applique à l'image seule, le panneau
					    posé dessus reste net. `cover` et non `contain` — c'est un décor, pas un document :
					    mieux vaut le rogner que laisser des bandes vides, d'autant que le cadre change de
					    forme avec l'appareil. */}
					<View
						style={{
							position: 'absolute',
							inset: 0,
							background: `url(${mapPreview}) center / cover no-repeat`,
							// Assez flou pour qu'on ne cherche pas à y lire des allées ou des noms : c'est ce
							// traitement, plus que la mention, qui empêche de le prendre pour le plan du
							// projet. On regarde une image avant d'en lire la légende.
							filter: 'saturate(.4) blur(3px)',
							// Le flou déborde de ses bords et laisse un liseré clair : on élargit la couche
							// pour que la zone floutée dépasse du cadre.
							margin: -4,
						}}
					/>

					<View
						style={{
							// En haut à droite : le seul coin libre dans les DEUX layouts — le panneau occupe
							// la gauche en bureau, tout le bas en mobile.
							position: 'absolute',
							right: 6,
							top: 6,
							padding: '2px 6px',
							borderRadius: 4,
							fontSize: 9,
							letterSpacing: '.02em',
							color: 'rgba(255, 255, 255, .95)',
							background: 'rgba(0, 0, 0, .45)',
						}}
					>
						Illustration
					</View>

				{/* Le panneau : flottant à gauche en bureau, feuille du bas en mobile — voir le commentaire
				    du composant.

				    Un `div` et non un `View` : `View` pose une transition sur ses styles, et ce panneau est
				    redessiné en continu par le formulaire qui l'entoure — le navigateur n'avance alors
				    jamais l'animation, et `bottom` restait bloqué à sa valeur de départ (les 12px du layout
				    bureau), laissant la feuille flotter au-dessus du bas du cadre. Même cause que le
				    défilement animé qui ne partait pas. */}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						position: 'absolute',
						background: '#FFFFFF',
						boxShadow: '0 4px 18px rgba(0, 0, 0, .25)',
						overflow: 'hidden',
						fontFamily: 'sans-serif',
						...(mobile ? {
							left: 0,
							right: 0,
							bottom: 0,
							maxHeight: '70%',
							borderRadius: '12px 12px 0 0',
						} : {
							top: 12,
							bottom: 12,
							left: 12,
							width: '52%',
							borderRadius: 12,
						}),
					}}
				>
					{/* La poignée n'existe qu'en feuille du bas — `display: none` en bureau côté carte. */}
					{mobile && (
						<View
							style={{
								flexShrink: 0,
								width: 28,
								height: 3,
								margin: '6px auto 0',
								borderRadius: 2,
								background: '#DDD',
							}}
						/>
					)}

					<View row centerV gap={9} p={12} style={{ flexShrink: 0 }}>
						{!!props.image && (
							<img
								src={corsImageSrc(props.image)}
								crossOrigin={storageImageCrossOrigin(props.image)}
								alt=""
								style={{
									width: 36,
									height: 36,
									flexShrink: 0,
									borderRadius: 6,
									objectFit: 'contain',
									background: '#F0F0F0',
								}}
							/>
						)}

						<View flex gap={2} style={{ minWidth: 0 }}>
							<View style={{ fontSize: 13, fontWeight: 700, color: '#222', lineHeight: 1.25, wordBreak: 'break-word' }}>
								{entry.title || 'Sans titre'}
							</View>

							{!!entry.subTitle && (
								<View style={{ fontSize: 10.5, color: '#999', wordBreak: 'break-word' }}>
									{entry.subTitle}
								</View>
							)}
						</View>

						<View
							center
							style={{
								flexShrink: 0,
								width: 22,
								height: 22,
								borderRadius: '50%',
								background: '#F0F0F0',
								color: '#666',
								fontSize: 13,
								lineHeight: 1,
							}}
						>
							×
						</View>
					</View>

					{/* Rien du tout sans champ — filet supérieur compris : le panneau réel n'ajoute son `<dl>`
					    que s'il a quelque chose à y mettre (voir `EntityPanel.show`). Une phrase expliquant
					    le vide aurait montré un élément qui n'existe pas en ligne, et un aperçu qui commente
					    ce qu'il montre cesse d'être un aperçu. */}
					{!!entry.fields.length && (
						<View
							gap={8}
							px={12}
							pb={12}
							style={{ borderTop: '1px solid #EEE', paddingTop: 10, overflowY: 'auto' }}
						>
							{groupConsecutiveImages(entry.fields).map(group => group[0]!.kind === 'image' ? (
								<View key={group[0]!.label} row wrap style={{ columnGap: 9, rowGap: 8 }}>
									{group.map(field => (
										<View key={field.label} gap={2}>
											<FieldLabel>{field.label}</FieldLabel>

											<Image src={field.value} width={54} borderRadius={6} preview />
										</View>
									))}
								</View>
							) : (
								<View key={group[0]!.label} gap={2}>
									<FieldLabel>{group[0]!.label}</FieldLabel>

									<View row centerV gap={6} style={{ fontSize: 11.5, color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
										{group[0]!.kind === 'color' && (
											<View
												w={12}
												h={12}
												br={3}
												style={{ flexShrink: 0, background: group[0]!.value, border: '1px solid #EEE' }}
											/>
										)}

										{group[0]!.kind === 'url' && isWebUrl(group[0]!.value) ? (
											<a href={group[0]!.value} target="_blank" rel="noopener noreferrer" style={{ color: '#2979FF', textDecoration: 'underline' }}>
												{group[0]!.value}
											</a>
										) : group[0]!.value}
									</View>
								</View>
							))}
						</View>
					)}
				</div>
			</div>
		</View>
	);
}

const FieldLabel = (props: { children: React.ReactNode }) => (
	<View style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#999' }}>
		{props.children}
	</View>
);

const isWebUrl = (value: string) => {
	try {
		return ['http:', 'https:'].includes(new URL(value).protocol);
	} catch {
		return false;
	}
};
